import React, { useRef, useState, useMemo, useEffect } from 'react';
import { Input, Button } from 'reactstrap';
import config from 'config';
import { RequestAttachment } from 'types/MatriculateRequest';

interface Props {
  required: boolean;
  name: string;
  attachs?: RequestAttachment[];
  accept?: string[];
  canUpdateAttachs?: boolean;
  disabled?: boolean;
  multiple?: boolean;
  maxFileSize?: number;
}

export default function UploaderMultiple({
  required,
  name,
  attachs = [],
  accept = ['image/jpeg', 'image/png', 'application/pdf'],
  canUpdateAttachs = true,
  disabled = false,
  multiple = false,
  maxFileSize = 9 * 1024 * 1024,
}: Props) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [newUrls, setNewUrls] = useState<string[]>([]);
  const [sizeError, setSizeError] = useState(false);
  const [formatError, setFormatError] = useState(false);

  const mimeTypeMap: Record<string, string> = {
    'image/jpeg': '.JPG / .JPEG',
    'image/png': '.PNG',
    'application/pdf': '.PDF',
    'application/msword': '.DOC (Word)',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.DOCX (Word)',
  };

  const existingFiles = useMemo(() => {
    return attachs
      .filter(a => a.attachment_type.startsWith(name))
      .map(a => ({
        id: a.id,
        url: `${config.baseUrl}/${a.url}`,
      }));
  }, [attachs, name]);

  useEffect(() => {
    return () => newUrls.forEach(url => URL.revokeObjectURL(url));
  }, [newUrls]);

  const selectFiles = () => {
    setSizeError(false);
    setFormatError(false);

    if (!fileInput.current?.files) return;
    const filesArray = Array.from(fileInput.current.files);
    if (filesArray.length === 0) return; // canceló

    for (const file of filesArray) {
      if (file.size > maxFileSize) {
        fileInput.current.value = '';
        setNewFiles([]);
        setNewUrls([]);
        setSizeError(true);
        return;
      }

      if (accept.length > 0 && !accept.includes(file.type)) {
        fileInput.current.value = '';
        setNewFiles([]);
        setNewUrls([]);
        setFormatError(true);
        return;
      }
    }

    // Liberamos URLs anteriores
    newUrls.forEach(url => URL.revokeObjectURL(url));

    const finalFiles = multiple ? filesArray : [filesArray[0]];
    const urls = finalFiles.map(file => URL.createObjectURL(file));

    setNewFiles(finalFiles);
    setNewUrls(urls);
  };

  const openPicker = () => fileInput.current?.click();

  const hasExisting = existingFiles.length > 0;
  const hasNew = newFiles.length > 0;

  const showInput = !hasExisting && !hasNew;

  return (
    <>
      <Input
        innerRef={fileInput}
        type="file"
        accept={accept.join(', ')}
        onChange={selectFiles}
        name={name}
        multiple={multiple}
        style={!showInput ? { display: 'none' } : {}}
        required={required && !hasExisting && !hasNew}
        disabled={disabled}
      />

      <label className={`required-text ${!sizeError ? 'hidden' : ''}`}>
        El peso del archivo no debe superar los { maxFileSize / 1024 / 1024 } MB
      </label>
      <label className={`required-text ${!formatError ? 'hidden' : ''}`}>
        Formato no permitido. Solo se aceptan:{' '}
        {accept
          .map(mime => mimeTypeMap[mime] || mime)
          .join(', ')}
      </label>

      {/* Nuevos archivos */}
      {hasNew &&
        newUrls.map((url, index) => (
          <div key={index} className="mb-2">
            <Button color="primary" onClick={() => window.open(url, '_blank')}>
              Ver adjunto {multiple ? index + 1 : ''}
            </Button>
          </div>
        ))}

      {/* Archivos existentes si no hay nuevos */}
      {!hasNew &&
        hasExisting &&
        existingFiles.map((file, index) => (
          <div key={file.id} className="mb-2">
            <Button color="primary" onClick={() => window.open(file.url, '_blank')}>
              Ver adjunto {multiple ? index + 1 : ''}
            </Button>
          </div>
        ))}

      {((canUpdateAttachs || showInput) && (hasExisting || hasNew)) && (
        <Button color="warning" onClick={openPicker}>Reemplazar archivo{!!multiple && 's'}</Button>
      )}
    </>
  );
}
